import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, X, Search, ChevronRight, Gauge, Trash2,
  ClipboardList, Target, CheckCircle, TrendingUp,
  Compass, Navigation, Building2, Flag, MapPin, Phone, Mail,
  User, Briefcase, Car, Clock, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ROUTE_META = {
  builder:      { name: "Builder's Route", icon: Compass, color: 'amber', gradient: 'from-amber-500 to-orange-600' },
  personal:     { name: 'Personal Lane', icon: Navigation, color: 'sky', gradient: 'from-sky-500 to-blue-600' },
  corporate:    { name: 'Executive Corridor', icon: Building2, color: 'emerald', gradient: 'from-emerald-500 to-green-600' },
  accelerator:  { name: 'The Accelerator', icon: Flag, color: 'red', gradient: 'from-red-500 to-rose-600' },
};

const STAGES = [
  { key: 'discovery', label: 'Discovery', icon: Search, color: 'blue' },
  { key: 'audit', label: 'Audit', icon: ClipboardList, color: 'yellow' },
  { key: 'strategy', label: 'Strategy', icon: Target, color: 'purple' },
  { key: 'complete', label: 'Complete', icon: CheckCircle, color: 'green' },
];

const STAGE_BORDER = { discovery: '#3b82f6', audit: '#eab308', strategy: '#a855f7', complete: '#22c55e' };

const ConsultingPipeline = () => {
  const navigate = useNavigate();
  const [stages, setStages] = useState({ discovery: [], audit: [], strategy: [], complete: [] });
  const [kpis, setKpis] = useState({});
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: 'include' })
      .then(r => { if (!r.ok) navigate('/admin/login'); })
      .catch(() => navigate('/admin/login'));
  }, [navigate]);

  const loadPipeline = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/consulting/pipeline`);
      const data = await res.json();
      setStages(data.stages || {});
      setKpis(data.kpis || {});
    } catch {}
  }, []);

  useEffect(() => { loadPipeline(); }, [loadPipeline]);

  const getClientName = (i) => i.fullName || i.client_name || 'Unknown';
  const getRoute = (i) => {
    const r = i.route || '';
    return ROUTE_META[r] || null;
  };
  const getFormLabel = (i) => {
    if (i.form_type === 'track_d') return 'Track D / ACCD';
    const rm = getRoute(i);
    return rm ? rm.name : (i.engagement_type || 'Intake');
  };

  const openDetail = (intake) => {
    setSelectedIntake(intake.intake_id);
    setDetailData(intake);
  };

  const moveStage = async (intakeId, newStage) => {
    await fetch(`${API}/api/consulting/intakes/${intakeId}/stage`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage: newStage })
    });
    toast.success(`Moved to ${newStage}`);
    loadPipeline();
    if (detailData?.intake_id === intakeId) {
      setDetailData(prev => ({ ...prev, stage: newStage }));
    }
  };

  const deleteIntake = async (intakeId) => {
    if (!window.confirm('Delete this intake? This cannot be undone.')) return;
    await fetch(`${API}/api/consulting/intakes/${intakeId}`, { method: 'DELETE' });
    toast.success('Deleted');
    setDetailData(null);
    setSelectedIntake(null);
    loadPipeline();
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="consulting-pipeline-page">
      {/* Header */}
      <header className="bg-black text-white py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-gray-300 hover:text-white" data-testid="pipeline-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                <MapPin className="h-5 w-5 text-red-500" /> Consulting Pipeline
              </h1>
              <p className="text-xs text-gray-500">Track clients from Discovery to Completion</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {STAGES.map(s => {
            const Icon = s.icon;
            return (
              <Card key={s.key} className="p-3" data-testid={`kpi-${s.key}`}>
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: STAGE_BORDER[s.key] + '20' }}>
                    <Icon className="h-4 w-4" style={{ color: STAGE_BORDER[s.key] }} />
                  </div>
                  <div>
                    <p className="text-xl font-bold">{kpis[s.key] || 0}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider">{s.label}</p>
                  </div>
                </div>
              </Card>
            );
          })}
          <Card className="p-3" data-testid="kpi-total">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center"><TrendingUp className="h-4 w-4 text-gray-600" /></div>
              <div><p className="text-xl font-bold">{kpis.total || 0}</p><p className="text-[10px] text-gray-500 uppercase tracking-wider">Total</p></div>
            </div>
          </Card>
        </div>

        {/* Kanban Board */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="grid grid-cols-4 gap-4 min-w-[900px] sm:min-w-0">
            {STAGES.map(s => (
              <div key={s.key}>
                <div className="flex items-center gap-2 mb-3 px-1">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STAGE_BORDER[s.key] }} />
                  <h3 className="font-bold text-xs uppercase tracking-wider text-gray-700">{s.label}</h3>
                  <span className="text-[10px] text-gray-400 ml-auto">{(stages[s.key] || []).length}</span>
                </div>
                <div className="space-y-2 min-h-[200px]">
                  {(stages[s.key] || []).map(intake => {
                    const rm = getRoute(intake);
                    const RouteIcon = rm?.icon || User;
                    const isSelected = selectedIntake === intake.intake_id;
                    return (
                      <Card
                        key={intake.intake_id}
                        className={`p-3 cursor-pointer transition-all hover:shadow-md border-l-[3px] ${isSelected ? 'ring-2 ring-red-500 bg-red-50' : ''}`}
                        style={{ borderLeftColor: STAGE_BORDER[s.key] }}
                        onClick={() => openDetail(intake)}
                        data-testid={`intake-card-${intake.intake_id}`}
                      >
                        <div className="flex items-start gap-2 mb-1.5">
                          <div className={`w-7 h-7 rounded-md bg-gradient-to-br ${rm?.gradient || 'from-gray-400 to-gray-500'} flex items-center justify-center shrink-0`}>
                            <RouteIcon className="h-3.5 w-3.5 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-sm truncate">{getClientName(intake)}</p>
                            <p className="text-[10px] text-gray-500 truncate">{getFormLabel(intake)}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[10px] text-gray-400">{intake.intake_id}</span>
                          <span className="text-[10px] text-gray-400">{new Date(intake.created_at).toLocaleDateString()}</span>
                        </div>
                      </Card>
                    );
                  })}
                  {(stages[s.key] || []).length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      <s.icon className="h-6 w-6 mx-auto mb-2 opacity-30" />
                      <p className="text-xs">No clients in {s.label}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detail Panel */}
        {detailData && (
          <Card className="mt-6 overflow-hidden" data-testid="intake-detail">
            <div className="bg-black text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {(() => {
                  const rm = getRoute(detailData);
                  const Icon = rm?.icon || User;
                  return (
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${rm?.gradient || 'from-gray-400 to-gray-500'} flex items-center justify-center`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                  );
                })()}
                <div>
                  <h3 className="text-lg font-bold">{getClientName(detailData)}</h3>
                  <p className="text-xs text-gray-400">{getFormLabel(detailData)} | {detailData.intake_id}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={detailData.stage || 'discovery'} onValueChange={v => moveStage(detailData.intake_id, v)}>
                  <SelectTrigger className="w-[130px] bg-gray-900 border-gray-700 text-white text-xs" data-testid="stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => deleteIntake(detailData.intake_id)} data-testid="delete-intake-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white" onClick={() => { setDetailData(null); setSelectedIntake(null); }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="p-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Contact Info */}
              <DetailSection title="Contact" icon={User}>
                <DRow label="Name" value={getClientName(detailData)} />
                <DRow label="Email" value={detailData.email} link={`mailto:${detailData.email}`} />
                <DRow label="Phone" value={detailData.phone} link={`tel:${detailData.phone}`} />
                <DRow label="City / State" value={detailData.cityState} />
                <DRow label="Best Time" value={detailData.bestTime} />
                <DRow label="Preferred Name" value={detailData.preferredName} />
              </DetailSection>

              {/* Business Info */}
              <DetailSection title="Business" icon={Briefcase}>
                <DRow label="Business Name" value={detailData.businessName || detailData.companyName} />
                <DRow label="Type" value={detailData.businessType} />
                <DRow label="Industry" value={detailData.industry} />
                <DRow label="Website" value={detailData.businessWebsite} />
                <DRow label="Title / Role" value={detailData.companyTitle} />
                <DRow label="Employees" value={detailData.employeeCount} />
                <DRow label="Entity" value={detailData.hasEntity} />
                <DRow label="EIN" value={detailData.einEstablished} />
                <DRow label="Bank Account" value={detailData.hasBankAccount} />
                <DRow label="Years in Business" value={detailData.yearsInBusiness} />
              </DetailSection>

              {/* Source */}
              <DetailSection title="Source & Referral" icon={MapPin}>
                <DRow label="How Found" value={detailData.howFound} />
                <DRow label="Referred By" value={detailData.referredBy} />
                <DRow label="Submitted" value={detailData.submitted_at ? new Date(detailData.submitted_at).toLocaleString() : detailData.created_at ? new Date(detailData.created_at).toLocaleString() : ''} />
                <DRow label="Form Type" value={detailData.form_type === 'track_d' ? 'Track D / ACCD' : 'Client Profile'} />
              </DetailSection>

              {/* Assessment */}
              <DetailSection title="Assessment" icon={ClipboardList}>
                <DRow label="Business Credit" value={detailData.businessCreditStatus} />
                <DRow label="Personal Credit" value={detailData.personalCreditRange} />
                <DRow label="Capital Available" value={detailData.availableCapital || detailData.capitalToInvest} />
                <DRow label="Fleet Access" value={detailData.fleetAccess} />
                <DRow label="Vehicle" value={detailData.currentVehicle || detailData.vehicleYMM} />
                <DRow label="Vehicle Status" value={detailData.vehicleOwnership} />
                <DRow label="Monthly Payment" value={detailData.monthlyPayment || detailData.vehiclePayment} />
                <DRow label="Vehicle Standard" value={detailData.vehicleStandard} />
                <DRow label="Satisfaction" value={detailData.vehicleSatisfaction} />
                {detailData.transportMode?.length > 0 && <DRow label="Transport Mode" value={detailData.transportMode.join(', ')} />}
                {detailData.painPoints?.length > 0 && <DRow label="Pain Points" value={detailData.painPoints.join(', ')} />}
                {detailData.currentTransportSetup?.length > 0 && <DRow label="Current Setup" value={detailData.currentTransportSetup.join(', ')} />}
                <DRow label="Monthly Transport Spend" value={detailData.monthlyTransportSpend} />
              </DetailSection>

              {/* Vision & Goals */}
              <DetailSection title="Vision & Goals" icon={Target}>
                <DRow label="Why ACE" value={detailData.whyAce} full />
                <DRow label="Business Vision" value={detailData.businessVision || detailData.bigPicture} full />
                <DRow label="Lifestyle Vision" value={detailData.lifestyleVision} full />
                {detailData.serviceTypes?.length > 0 && <DRow label="Service Types" value={detailData.serviceTypes.join(', ')} />}
                {detailData.serviceFocus?.length > 0 && <DRow label="Service Focus" value={detailData.serviceFocus.join(', ')} />}
                <DRow label="Operational Role" value={detailData.operationalRole} />
                <DRow label="Target Market" value={detailData.targetMarket || detailData.targetCity} />
                <DRow label="Target Customer" value={detailData.targetCustomer || detailData.idealClient} />
                <DRow label="Dream Vehicle" value={detailData.dreamVehicle} />
                <DRow label="Acquisition Method" value={detailData.acquisitionMethod} />
                <DRow label="Monthly Budget" value={detailData.monthlyBudget} />
                {detailData.priorityGoals?.length > 0 && <DRow label="Priority Goals" value={detailData.priorityGoals.join(', ')} />}
                {detailData.transportNeeds?.length > 0 && <DRow label="Transport Needs" value={detailData.transportNeeds.join(', ')} />}
                <DRow label="Transition Timeline" value={detailData.transitionTimeline} />
                <DRow label="First Hire Plan" value={detailData.firstHirePlan} />
              </DetailSection>

              {/* Engagement */}
              <DetailSection title="Engagement" icon={DollarSign}>
                <DRow label="Timeline" value={detailData.timeline} />
                <DRow label="Support Type" value={detailData.supportType || detailData.engagementPref} />
                <DRow label="Membership" value={detailData.membershipInterest} />
                <DRow label="Investment Range" value={detailData.investmentRange} />
                <DRow label="Ready to Invest" value={detailData.readyToInvest} />
                <DRow label="Final Notes" value={detailData.finalNotes || detailData.anythingElse} full />
              </DetailSection>

              {/* Track D Specifics */}
              {detailData.form_type === 'track_d' && (
                <DetailSection title="ACCD Readiness" icon={Flag}>
                  <DRow label="Years Driving" value={detailData.yearsDriving} />
                  <DRow label="Weekly Trips" value={detailData.weeklyTrips} />
                  <DRow label="Monthly Gross" value={detailData.monthlyGross} />
                  <DRow label="Monthly Net" value={detailData.monthlyNet} />
                  {detailData.operationType?.length > 0 && <DRow label="Operation Type" value={detailData.operationType.join(', ')} />}
                  <DRow label="Finances Separated" value={detailData.financesSeparated} />
                  <DRow label="Previous Attempts" value={detailData.previousAttempts} />
                  <DRow label="Previous Details" value={detailData.previousDetails} full />
                  <RatingBar label="Entity Knowledge" val={detailData.knowledgeEntity} />
                  <RatingBar label="Credit Knowledge" val={detailData.knowledgeCredit} />
                  <RatingBar label="Pricing Knowledge" val={detailData.knowledgePricing} />
                  <RatingBar label="Corporate Accounts" val={detailData.knowledgeCorporate} />
                  <RatingBar label="Driver Mgmt" val={detailData.knowledgeDrivers} />
                  <RatingBar label="Fleet Acquisition" val={detailData.knowledgeFleet} />
                  <RatingBar label="Service Standards" val={detailData.knowledgeService} />
                  <RatingBar label="Marketing" val={detailData.knowledgeMarketing} />
                  <div className="border-t pt-2 mt-2 space-y-1">
                    <RatingBar label="Program Commitment" val={detailData.commitProgram} />
                    <RatingBar label="Financial Commitment" val={detailData.commitFinancial} />
                    <RatingBar label="Transition Readiness" val={detailData.commitTransition} />
                    <RatingBar label="Coachability" val={detailData.commitCoachable} />
                    <RatingBar label="Urgency" val={detailData.commitUrgency} />
                  </div>
                </DetailSection>
              )}

              {/* Legacy Fields (old intakes) */}
              {detailData.client_name && !detailData.fullName && (
                <DetailSection title="Legacy Intake" icon={ClipboardList}>
                  <DRow label="Client Name" value={detailData.client_name} />
                  <DRow label="Engagement Type" value={detailData.engagement_type} />
                  <DRow label="Current Vehicles" value={detailData.current_vehicles} />
                  <DRow label="Monthly Budget" value={detailData.monthly_transport_budget} />
                  <DRow label="Goals" value={detailData.goals} full />
                  <DRow label="Timeline" value={detailData.timeline} />
                  <DRow label="Notes" value={detailData.notes} full />
                </DetailSection>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

/* ═══ Sub-components ═══ */
const DetailSection = ({ title, icon: Icon, children }) => {
  const items = React.Children.toArray(children).filter(c => c.props?.value);
  if (items.length === 0) return null;
  return (
    <div>
      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" /> {title}
      </h4>
      <div className="space-y-1.5">{items}</div>
    </div>
  );
};

const DRow = ({ label, value, link, full }) => {
  if (!value) return null;
  const content = link
    ? <a href={link} className="text-red-500 hover:underline">{value}</a>
    : <span className="text-gray-900">{value}</span>;
  return (
    <div className={full ? '' : 'flex items-start justify-between gap-2'}>
      <span className="text-xs text-gray-500 shrink-0">{label}</span>
      <span className={`text-xs font-medium ${full ? 'block mt-0.5' : 'text-right'}`}>{content}</span>
    </div>
  );
};

const RatingBar = ({ label, val }) => {
  if (!val && val !== 0) return null;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-gray-500 w-28 shrink-0 truncate">{label}</span>
      <div className="flex gap-0.5 flex-1">
        {[1,2,3,4,5].map(n => (
          <div key={n} className={`h-2 flex-1 rounded-sm ${n <= val ? 'bg-red-500' : 'bg-gray-200'}`} />
        ))}
      </div>
      <span className="text-[10px] font-bold text-gray-600 w-4 text-right">{val}</span>
    </div>
  );
};

export default ConsultingPipeline;
